<script setup lang="ts">
import { computed } from 'vue';
import { enrollmentStatusMeta } from 'src/modules/School/utilities/enrollmentStatusMeta';

/**
 * The enrollment status indicator: a colored dot and a label, nothing else.
 *
 * Deliberately NOT a pill or a chip — those read as pressable, and this is
 * output, not a control. Anything the user can act on sits next to it as a real
 * button. Shared by the teacher's class roster and the student's dashboard cards
 * so one status looks the same wherever it appears.
 */
const props = defineProps<{
  status?: string | undefined;
  /** Reserves label width so dots and any trailing controls line up across rows. */
  align?: boolean | undefined;
}>();

const meta = computed(() => enrollmentStatusMeta(props.status));
</script>

<template>
  <div
    class="enrollment-status-badge"
    :class="[`text-${meta.color}`, { 'enrollment-status-badge--align': align }]"
  >
    <span
      class="enrollment-status-badge__dot"
      :class="`bg-${meta.color}`"
    />
    <span class="enrollment-status-badge__label">
      {{ $t(meta.labelKey) }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.enrollment-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
}

.enrollment-status-badge--align {
  min-width: 84px;
}

.enrollment-status-badge__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}
</style>
